// Frame 11's DIVERGENCE MAP card (33:1499) — PRD 16 §5 B1, "`tenant` is
// defined three incompatible ways across nine docs".
//
// Source is `GET /dashboards/{org}/health`'s `inconsistent` findings, which is
// exactly the set `corpus/health.py:130` already computes for the monthly
// report. Ranked by blast radius first (documents touched) and disagreement
// second, per B1's `(1 − confidence) × doc_count`.
//
// WHAT IS DELIBERATELY NOT DRAWN: the ACCEPT / REJECT pair on each drawn card.
// Those act on a corpus MergeProposal, the reviewer surface for that is
// `/corpora/:corpusId/merges`, and no endpoint reachable from here performs the
// write. A button that looks live and does nothing is worse than a link to the
// screen that does the work, so the card footer links out instead.

import { Link } from "@tanstack/react-router";
import { Badge, Card, EmptyState } from "../../ui";
import type { HealthFinding } from "../../dashboard/api";
import { shownOf } from "./format";
import "./analytics.css";

/** A work-list, not an inventory — same reasoning as `corpus/health.py`'s
 *  TOP_N. The total is always stated beside it. */
const MAX_ROWS = 6;
const MAX_DOC_CHIPS = 6;

/** Rank by `(1 − confidence) × doc_count`, which is B1's own formula.
 *
 * The health finding does not carry a confidence — `VocabFinding.score` for an
 * `inconsistent` row is `float(len(docs))`, the document count again, so
 * ranking on it was a degenerate tiebreak that made the order doc-count alone.
 * The confidence comes from the corpus glossary instead, via the impact-radius
 * payload, keyed on the concept name (`corpus/health.py` sets a finding's
 * `term` to the entity's `name`).
 *
 * **An unknown confidence ranks as maximally divergent, not as zero-divergent.**
 * A finding with no canonical entity behind it comes from a *pending*
 * fuzzy-match proposal — a disagreement nobody has adjudicated — so surfacing
 * it first is the intended reading, not a fallback that happens to work.
 */
export function rankDivergence(
  findings: HealthFinding[],
  confidenceByTerm: Map<string, number | null> = new Map(),
): HealthFinding[] {
  const weight = (finding: HealthFinding) =>
    (1 - (confidenceByTerm.get(finding.term) ?? 0)) * finding.doc_ids.length;
  return findings
    .filter((finding) => finding.kind === "inconsistent")
    .sort(
      (a, b) =>
        weight(b) - weight(a) ||
        b.doc_ids.length - a.doc_ids.length ||
        a.term.localeCompare(b.term),
    );
}

export type DivergenceMapProps = {
  findings: HealthFinding[];
  /** Corpus whose merge proposals sit behind these rows. One corpus per org is
   *  the same assumption `dashboard/api.ts` and `reports/monthly.py` make. */
  corpusId: string;
  /** Concept name -> cluster confidence, from the impact-radius payload. Absent
   *  leaves the ranking on blast radius alone; see `rankDivergence`. */
  confidenceByTerm?: Map<string, number | null>;
};

export function DivergenceMap({
  findings,
  corpusId,
  confidenceByTerm,
}: DivergenceMapProps) {
  const ranked = rankDivergence(findings, confidenceByTerm);
  const shown = ranked.slice(0, MAX_ROWS);

  return (
    <Card
      header="Divergence map"
      footer={
        <div className="maisight-analytics-card-footer">
          <span className="type-bodyS">
            {shownOf(shown.length, ranked.length, "divergent concept")}
          </span>
          <Link
            className="type-labelSm maisight-analytics-row-link"
            to="/corpora/$corpusId/merges"
            params={{ corpusId }}
          >
            Review merge proposals
          </Link>
        </div>
      }
    >
      {ranked.length === 0 ? (
        <EmptyState
          headline="No contradictions found"
          body="Every concept your published documents define is defined the same way in all of them. This panel fills in when two documents disagree."
        />
      ) : (
        <ol className="maisight-analytics-divergence" aria-label="Divergent concepts">
          {shown.map((finding) => (
            <li key={finding.term} className="maisight-analytics-divergence-row">
              <div className="maisight-analytics-divergence-head">
                <span className="maisight-analytics-term type-numSm">
                  {finding.term}
                </span>
                <span className="type-labelSm maisight-analytics-muted">
                  {finding.doc_ids.length} documents
                </span>
              </div>
              <div className="maisight-analytics-chips">
                {finding.doc_ids.slice(0, MAX_DOC_CHIPS).map((docId) => (
                  <Badge key={docId}>{docId}</Badge>
                ))}
                {finding.doc_ids.length > MAX_DOC_CHIPS ? (
                  <Badge>{`+${finding.doc_ids.length - MAX_DOC_CHIPS} more`}</Badge>
                ) : null}
              </div>
              <p className="maisight-analytics-quote type-bodyS">{finding.detail}</p>
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
}
